import { z } from "zod";
import { RichText } from "../RichText";
import { globalSectionSchema, backgroundStyleEnum, NARROW_WIDTH } from "../globalProps";
import { CARD_SHADOW } from "../cardStyles";
import { SectionShell } from "../SectionShell";

export const schema = globalSectionSchema.extend({
  layout: z
    .enum(["prose", "heading-text", "two-column", "pull-quote", "divider"])
    .default("prose"),
  heading: z.string().optional(),
  body: z.string().default(""),
  bodyRight: z.string().optional(),
  alignment: z.enum(["left", "center"]).default("left"),
  dividerStyle: z.enum(["line", "dots", "space"]).default("line"),
  backgroundStyle: backgroundStyleEnum.default("light"),
  intro: z.string().optional(),
  outro: z.string().optional(),
  columnCardStyle: z.enum(["none", "elevated", "outlined"]).default("none"),
});

export type Props = z.infer<typeof schema>;
export const defaults: Props = schema.parse({});

/* Per-layout container width maps */
const TWO_COLUMN_WIDTH = {
  contained: "max-w-5xl mx-auto px-6",
  wide:      "max-w-6xl mx-auto px-6",
  full:      "max-w-7xl mx-auto px-6",
} as const;

const QUOTE_DIVIDER_WIDTH = {
  contained: "max-w-4xl mx-auto px-6",
  wide:      "max-w-5xl mx-auto px-6",
  full:      "max-w-6xl mx-auto px-6",
} as const;

/** Alignment utility: maps alignment prop to text-align class. */
const ALIGN_CLASS = { left: "text-start", center: "text-center" } as const;

function Prose({ safe }: { safe: Props }) {
  const body = safe.body || "Add your content here. Use the text tools to format headings, paragraphs, bold, italic, and links.";
  const container = NARROW_WIDTH[safe.width];
  const align = ALIGN_CLASS[safe.alignment];
  return (
    <div className={`${container} ${align}`}>
      <RichText
        as="div"
        html={body}
        className="prose prose-lg leading-relaxed"
        data-prop="body"
      />
    </div>
  );
}

function HeadingText({ safe }: { safe: Props }) {
  const heading = safe.heading || "Our Mission";
  const body =
    safe.body ||
    "We believe everyone deserves access to quality professional guidance. Our firm was founded on the principle that expert advice should be straightforward, accessible, and tailored to each client's unique circumstances.";
  const container = NARROW_WIDTH[safe.width];
  const align = ALIGN_CLASS[safe.alignment];
  return (
    <div className={`${container} ${align}`}>
      <RichText
        as={safe.headingLevel}
        html={heading}
        className="text-3xl @md:text-[2.5rem] font-bold mb-6"
        data-prop="heading"
      />
      <RichText
        as="div"
        html={body}
        className="text-lg leading-relaxed opacity-90"
        data-prop="body"
      />
    </div>
  );
}

/** Column card style classes for two-column layout. */
const COLUMN_CARD_CLASSES = {
  none: "",
  elevated: `${CARD_SHADOW} rounded-lg p-6 bg-white text-text`,
  outlined: "border border-current/10 rounded-lg p-6",
} as const;

function TwoColumn({ safe }: { safe: Props }) {
  const heading = safe.heading || "Our Approach";
  const body =
    safe.body ||
    "We start every engagement by listening. Understanding your goals, constraints, and priorities allows us to develop a strategy that actually fits your situation -- not a one-size-fits-all template.";
  const bodyRight =
    safe.bodyRight ||
    "Throughout the process, you will have direct access to your advisor. We believe in building relationships, not processing cases. Your questions are always welcome, and you will never be left wondering what comes next.";
  const container = TWO_COLUMN_WIDTH[safe.width];
  const align = ALIGN_CLASS[safe.alignment];
  const cardClass = COLUMN_CARD_CLASSES[safe.columnCardStyle] || "";
  return (
    <div className={`${container} ${align}`}>
      {heading && (
        <RichText
          as={safe.headingLevel}
          html={heading}
          className="text-3xl @md:text-[2.5rem] font-bold mb-8"
          data-prop="heading"
        />
      )}
      {safe.intro && (
        <RichText
          as="div"
          html={safe.intro}
          className="text-lg leading-relaxed opacity-90 mb-8"
          data-prop="intro"
        />
      )}
      <div className="grid grid-cols-1 @md:grid-cols-2 gap-8 @md:gap-12">
        {cardClass ? (
          <>
            <div className={cardClass}>
              <RichText
                as="div"
                html={body}
                className="text-lg leading-relaxed opacity-90"
                data-prop="body"
              />
            </div>
            <div className={cardClass}>
              <RichText
                as="div"
                html={bodyRight}
                className="text-lg leading-relaxed opacity-90"
                data-prop="bodyRight"
              />
            </div>
          </>
        ) : (
          <>
            <RichText
              as="div"
              html={body}
              className="text-lg leading-relaxed opacity-90"
              data-prop="body"
            />
            <RichText
              as="div"
              html={bodyRight}
              className="text-lg leading-relaxed opacity-90"
              data-prop="bodyRight"
            />
          </>
        )}
      </div>
      {safe.outro && (
        <RichText
          as="div"
          html={safe.outro}
          className="text-lg leading-relaxed opacity-90 mt-8"
          data-prop="outro"
        />
      )}
    </div>
  );
}

function PullQuote({ safe }: { safe: Props }) {
  const body =
    safe.body || "The right guidance at the right time can change everything.";
  const container = QUOTE_DIVIDER_WIDTH[safe.width];
  return (
    <div className={`${container} text-center`}>
      <blockquote className="text-2xl @md:text-3xl font-light italic leading-relaxed">
        <RichText html={body} data-prop="body" />
      </blockquote>
      {safe.heading && (
        <RichText
          as="p"
          html={safe.heading}
          className="mt-6 text-base text-text-muted font-medium"
          data-prop="heading"
        />
      )}
    </div>
  );
}

function Divider({ safe }: { safe: Props }) {
  const style = safe.dividerStyle;
  const container = QUOTE_DIVIDER_WIDTH[safe.width];
  return (
    <div className={container}>
      {style === "line" && (
        <hr className="border-t border-current opacity-15" />
      )}
      {style === "dots" && (
        <div className="flex justify-center gap-3">
          <span className="w-1.5 h-1.5 rounded-full bg-current opacity-30" />
          <span className="w-1.5 h-1.5 rounded-full bg-current opacity-30" />
          <span className="w-1.5 h-1.5 rounded-full bg-current opacity-30" />
        </div>
      )}
      {/* "space" style renders no visible content -- just the section padding */}
    </div>
  );
}

export function Component(props: Props) {
  const result = schema.safeParse(props);
  const safe = result.success ? result.data : defaults;

  const isDivider = safe.layout === "divider";
  // Force compact density for divider layout
  const globals = isDivider ? { ...safe, density: "compact" as const } : safe;

  return (
    <SectionShell globals={globals} bare>
      {safe.layout === "prose" && <Prose safe={safe} />}
      {safe.layout === "heading-text" && <HeadingText safe={safe} />}
      {safe.layout === "two-column" && <TwoColumn safe={safe} />}
      {safe.layout === "pull-quote" && <PullQuote safe={safe} />}
      {safe.layout === "divider" && <Divider safe={safe} />}
    </SectionShell>
  );
}
